/**
 * Part Need Dismiss Dialog (#725)
 *
 * Marks a parts-needs-cockpit row as "no order needed" with a required
 * reason, or undoes that (restore) — the only two actions this component
 * exposes.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, TextField, Typography } from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';

interface PartNeedDismissDialogProps {
  open: boolean;
  onClose: () => void;
  partName: string;
  onDismiss: (reason: string) => void;
  isLoading?: boolean;
}

export function PartNeedDismissDialog({ open, onClose, partName, onDismiss, isLoading }: PartNeedDismissDialogProps) {
  const { t } = useTranslation('inventory');
  const { isDirty, markDirty } = useFormDirty(open);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setError('');
    }
  }, [open]);

  const handleSubmit = () => {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setError(t('partsDashboard.dismiss.reasonRequired'));
      return;
    }
    onDismiss(trimmed);
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('partsDashboard.dismiss.title')}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      submitLabel={t('partsDashboard.dismiss.confirm')}
      submitColor="warning"
      isLoading={isLoading}
      maxWidth="xs"
    >
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('partsDashboard.dismiss.description', { partName })}
        </Typography>
        <TextField
          label={t('partsDashboard.dismiss.reasonLabel')}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            markDirty();
            if (error) setError('');
          }}
          fullWidth
          multiline
          rows={3}
          autoFocus
          error={!!error}
          helperText={error}
        />
      </DialogContent>
    </FormDialog>
  );
}
